import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  COOKIE_CONSENT_VERSION,
  type CookieConsentRecord,
  isConsentDecisionNeeded,
  parseCookieConsentValue,
  readGlobalPrivacyControl,
  readStoredCookieConsentValue,
  resolveAnalyticsAllowed,
  subscribeToCookieConsent,
  writeCookieConsent,
} from '../services/cookieConsent';
import { disableGoogleAnalytics, enableGoogleAnalytics } from '../services/googleAnalytics';

export interface CookieConsentContextValue {
  analyticsAllowed: boolean;
  globalPrivacyControlDetected: boolean;
  isDecisionNeeded: boolean;
  acceptAnalytics: () => void;
  rejectAnalytics: () => void;
  saveChoices: (choices: { analytics: boolean }) => void;
  isPreferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const storedValue = useSyncExternalStore(subscribeToCookieConsent, readStoredCookieConsentValue);
  // Holds the choice for the page when storage refuses the write.
  const [inPageRecord, setInPageRecord] = useState<CookieConsentRecord | null>(null);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [globalPrivacyControlDetected] = useState(() => readGlobalPrivacyControl(window.navigator));

  const storedRecord = useMemo(
    () => parseCookieConsentValue(storedValue, new Date()),
    [storedValue],
  );
  const record = inPageRecord ?? storedRecord;
  const analyticsAllowed = resolveAnalyticsAllowed({
    record,
    gpc: globalPrivacyControlDetected,
  });

  // The only place Google Analytics is switched on or off. It also runs on
  // first load, which clears `_ga*` cookies left without consent.
  useEffect(() => {
    if (analyticsAllowed) {
      enableGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }
  }, [analyticsAllowed]);

  const saveChoices = useCallback(
    ({ analytics }: { analytics: boolean }) => {
      const newRecord: CookieConsentRecord = {
        version: COOKIE_CONSENT_VERSION,
        analytics,
        decidedAt: new Date().toISOString(),
        gpc: globalPrivacyControlDetected,
      };
      setInPageRecord(writeCookieConsent(newRecord) ? null : newRecord);
    },
    [globalPrivacyControlDetected],
  );
  const openPreferences = useCallback(() => setIsPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setIsPreferencesOpen(false), []);
  const acceptAnalytics = useCallback(() => saveChoices({ analytics: true }), [saveChoices]);
  const rejectAnalytics = useCallback(() => saveChoices({ analytics: false }), [saveChoices]);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      analyticsAllowed,
      globalPrivacyControlDetected,
      isDecisionNeeded: isConsentDecisionNeeded({ record, gpc: globalPrivacyControlDetected }),
      acceptAnalytics,
      rejectAnalytics,
      saveChoices,
      isPreferencesOpen,
      openPreferences,
      closePreferences,
    }),
    [
      analyticsAllowed,
      globalPrivacyControlDetected,
      record,
      acceptAnalytics,
      rejectAnalytics,
      saveChoices,
      isPreferencesOpen,
      openPreferences,
      closePreferences,
    ],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}
